import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../stores/authStore';
import { getPhoneNumbers, addRecharge, deleteRecharge } from '../services/api';
import Navbar from '../components/Navbar';
import MobileNavbar from '../components/MobileNavbar';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import RechargeForm from '../components/phone/RechargeForm';
import { Search, Phone, Wifi, IndianRupee, Trash2, Layers } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY_RECHARGE = { amount: '', commission: '', paymentMethod: 'PhonePe', date: '', notes: '' };

const PhoneNumbersPage = () => {
  const user = useAuthStore((s) => s.user);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [networkFilter, setNetworkFilter] = useState('');
  const [showRechargeDialog, setShowRechargeDialog] = useState(false);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [rechargeForm, setRechargeForm] = useState(EMPTY_RECHARGE);
  const [submitting, setSubmitting] = useState(false);
  // Bulk recharge state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [bulkForm, setBulkForm] = useState(EMPTY_RECHARGE);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });

  const fetchPhoneNumbers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getPhoneNumbers();
      setPhoneNumbers(res.data);
    } catch (err) {
      console.error('Failed to load phone numbers:', err.message);
      toast.error('Failed to load phone numbers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPhoneNumbers(); }, [fetchPhoneNumbers]);

  const filtered = phoneNumbers.filter(p => {
    if (networkFilter && p.network !== networkFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      return [p.phoneNumber, p.vendorName, p.bankName, p.companyName, p.network].join(' ').toLowerCase().includes(s);
    }
    return true;
  });

  const networks = [...new Set(phoneNumbers.map(p => p.network).filter(Boolean))];
  const totalRechargeAmount = filtered.reduce((sum, p) => sum + (p.totalRechargeAmount || 0), 0);

  const formatMoney = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);

  // Unique key for each phone row
  const phoneKey = (phone, idx) => `${phone.bankId}-${phone.phoneNumber}-${idx}`;

  // Selection helpers
  const toggleSelect = (key) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((p, i) => phoneKey(p, i))));
    }
  };

  const selectedPhones = filtered.filter((p, i) => selectedIds.has(phoneKey(p, i)));

  // Single recharge dialog
  const openRechargeDialog = (phone) => {
    setSelectedPhone(phone);
    setRechargeForm(EMPTY_RECHARGE);
    setShowRechargeDialog(true);
  };

  const handleAddRecharge = async () => {
    if (!rechargeForm.amount || Number(rechargeForm.amount) <= 0) {
      toast.error('Please enter a valid recharge amount');
      return;
    }
    setSubmitting(true);
    try {
      await addRecharge(selectedPhone.vendorId, selectedPhone.bankId, {
        amount: Number(rechargeForm.amount),
        commission: Number(rechargeForm.commission) || 0,
        paymentMethod: rechargeForm.paymentMethod,
        date: rechargeForm.date,
        notes: rechargeForm.notes,
      });
      toast.success('Recharge added successfully');
      setRechargeForm(EMPTY_RECHARGE);
      await fetchPhoneNumbers();
      const fresh = (await getPhoneNumbers()).data;
      const updated = fresh.find(p => p.bankId === selectedPhone.bankId && p.phoneNumber === selectedPhone.phoneNumber);
      if (updated) setSelectedPhone(updated);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add recharge');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteRecharge = async (rechargeId) => {
    try {
      await deleteRecharge(selectedPhone.vendorId, selectedPhone.bankId, rechargeId);
      toast.success('Recharge deleted');
      await fetchPhoneNumbers();
      const fresh = (await getPhoneNumbers()).data;
      const updated = fresh.find(p => p.bankId === selectedPhone.bankId && p.phoneNumber === selectedPhone.phoneNumber);
      if (updated) setSelectedPhone(updated);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete recharge');
    }
  };

  // Bulk recharge
  const openBulkDialog = () => {
    setBulkForm(EMPTY_RECHARGE);
    setBulkProgress({ done: 0, total: 0 });
    setShowBulkDialog(true);
  };

  const handleBulkRecharge = async () => {
    if (!bulkForm.amount || Number(bulkForm.amount) <= 0) {
      toast.error('Please enter a valid recharge amount');
      return;
    }
    // De-dupe by bankId so we don't recharge the same bank twice
    const uniqueBanks = new Map();
    selectedPhones.forEach(p => {
      if (!uniqueBanks.has(p.bankId)) uniqueBanks.set(p.bankId, p);
    });
    const targets = Array.from(uniqueBanks.values());

    setBulkSubmitting(true);
    setBulkProgress({ done: 0, total: targets.length });

    let successCount = 0;
    let failCount = 0;
    for (const phone of targets) {
      try {
        await addRecharge(phone.vendorId, phone.bankId, {
          amount: Number(bulkForm.amount),
          commission: Number(bulkForm.commission) || 0,
          paymentMethod: bulkForm.paymentMethod,
          date: bulkForm.date,
          notes: bulkForm.notes,
        });
        successCount++;
      } catch (err) {
        console.error(`Bulk recharge failed for bank ${phone.bankId}:`, err.message);
        failCount++;
      }
      setBulkProgress(prev => ({ ...prev, done: prev.done + 1 }));
    }

    setBulkSubmitting(false);
    if (failCount === 0) {
      toast.success(`Recharge added to ${successCount} bank(s) successfully`);
    } else {
      toast.warning(`${successCount} succeeded, ${failCount} failed`);
    }
    setShowBulkDialog(false);
    setSelectedIds(new Set());
    await fetchPhoneNumbers();
  };

  const canEdit = user?.role === 'Admin' || user?.role === 'Sub Admin' || user?.role === 'Agent';

  return (
    <>
      <div className="hidden md:block"><Navbar /></div>
      <div className="md:hidden"><MobileNavbar /></div>
      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6 pt-20 md:pt-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="phone-numbers-title">Phone Numbers</h1>
            <p className="text-sm text-slate-500 mt-1">All SIM numbers across banks and their recharges</p>
          </div>
          <div className="flex gap-3 flex-wrap items-center">
            {canEdit && selectedIds.size > 0 && (
              <Button onClick={openBulkDialog} className="gap-2 bg-indigo-600 hover:bg-indigo-700" data-testid="bulk-recharge-btn">
                <Layers className="w-4 h-4" />
                Bulk Recharge ({selectedIds.size})
              </Button>
            )}
            <Card className="px-4 py-2">
              <div className="text-xs text-slate-500">Total Numbers</div>
              <div className="text-lg font-bold text-slate-900">{filtered.length}</div>
            </Card>
            <Card className="px-4 py-2">
              <div className="text-xs text-slate-500">Total Recharges</div>
              <div className="text-lg font-bold text-emerald-600">{formatMoney(totalRechargeAmount)}</div>
            </Card>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search by number, vendor, bank, company..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="phone-search" />
          </div>
          <Select value={networkFilter || "all-networks"} onValueChange={(val) => setNetworkFilter(val === "all-networks" ? "" : val)}>
            <SelectTrigger className="w-40" data-testid="network-filter"><SelectValue placeholder="Network" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all-networks">All Networks</SelectItem>
              {networks.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading phone numbers...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      {canEdit && (
                        <th className="w-10 p-3">
                          <Checkbox
                            checked={filtered.length > 0 && selectedIds.size === filtered.length}
                            onCheckedChange={toggleSelectAll}
                            data-testid="select-all-checkbox"
                          />
                        </th>
                      )}
                      <th className="text-left p-3 text-xs font-semibold text-slate-600">Phone Number</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-600">Network</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-600">SIM Location</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-600">Vendor</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-600">Company / Bank</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-600">Bank Status</th>
                      <th className="text-right p-3 text-xs font-semibold text-slate-600">Recharges</th>
                      <th className="text-right p-3 text-xs font-semibold text-slate-600">Total Amount</th>
                      <th className="text-center p-3 text-xs font-semibold text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((phone, idx) => {
                      const key = phoneKey(phone, idx);
                      return (
                        <tr key={key} className={`border-b hover:bg-slate-50 ${selectedIds.has(key) ? 'bg-indigo-50' : ''}`} data-testid={`phone-row-${idx}`}>
                          {canEdit && (
                            <td className="w-10 p-3">
                              <Checkbox
                                checked={selectedIds.has(key)}
                                onCheckedChange={() => toggleSelect(key)}
                                data-testid={`select-phone-${idx}`}
                              />
                            </td>
                          )}
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <Phone className="w-4 h-4 text-blue-500" />
                              <span className="font-mono text-sm font-medium">{phone.phoneNumber}</span>
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-1">
                              <Wifi className="w-3 h-3 text-slate-400" />
                              <span className="text-sm">{phone.network || '-'}</span>
                            </div>
                          </td>
                          <td className="p-3 text-sm text-slate-600">{phone.simLocation || '-'}</td>
                          <td className="p-3 text-sm text-slate-700">{phone.vendorName}</td>
                          <td className="p-3">
                            <div className="text-sm font-medium">{phone.companyName || '-'}</div>
                            <div className="text-xs text-slate-500">{phone.bankName}</div>
                          </td>
                          <td className="p-3">
                            <Badge variant="outline" className="text-xs">{phone.bankStatus}</Badge>
                          </td>
                          <td className="p-3 text-right text-sm">{phone.totalRecharges}</td>
                          <td className="p-3 text-right text-sm font-medium text-emerald-700">{formatMoney(phone.totalRechargeAmount)}</td>
                          <td className="p-3 text-center">
                            <Button variant="outline" size="sm" onClick={() => openRechargeDialog(phone)} data-testid={`manage-recharges-${idx}`} className="gap-1 text-xs">
                              <IndianRupee className="w-3 h-3" /> Manage
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {filtered.length === 0 && <div className="p-8 text-center text-slate-500">No phone numbers found</div>}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Single Recharge Management Dialog */}
      <Dialog open={showRechargeDialog} onOpenChange={setShowRechargeDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-blue-500" />
              Recharges - {selectedPhone?.phoneNumber}
            </DialogTitle>
          </DialogHeader>
          {selectedPhone && (
            <div className="space-y-5">
              <div className="flex gap-4 text-sm flex-wrap bg-slate-50 p-3 rounded-lg">
                <span className="text-slate-500">Vendor: <strong>{selectedPhone.vendorName}</strong></span>
                <span className="text-slate-500">Bank: <strong>{selectedPhone.bankName}</strong></span>
                <span className="text-slate-500">Company: <strong>{selectedPhone.companyName || '-'}</strong></span>
                <span className="text-slate-500">Network: <strong>{selectedPhone.network || '-'}</strong></span>
              </div>
              {canEdit && (
                <RechargeForm form={rechargeForm} setForm={setRechargeForm} onSubmit={handleAddRecharge} submitting={submitting} testIdPrefix="recharge" />
              )}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Recharge History ({selectedPhone.recharges.length})</h3>
                {selectedPhone.recharges.length > 0 ? (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-slate-50">
                            <th className="text-left p-2 text-xs font-semibold">#</th>
                            <th className="text-left p-2 text-xs font-semibold">Date</th>
                            <th className="text-left p-2 text-xs font-semibold">Method</th>
                            <th className="text-right p-2 text-xs font-semibold">Amount</th>
                            <th className="text-right p-2 text-xs font-semibold">Commission</th>
                            <th className="text-left p-2 text-xs font-semibold">Notes</th>
                            {canEdit && <th className="text-center p-2 text-xs font-semibold">Del</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {selectedPhone.recharges.map((r, i) => (
                            <tr key={r.id || i} className="border-b hover:bg-slate-50">
                              <td className="p-2 text-slate-500">{i + 1}</td>
                              <td className="p-2">{r.date || '-'}</td>
                              <td className="p-2"><Badge variant="outline" className="text-xs">{r.paymentMethod || '-'}</Badge></td>
                              <td className="p-2 text-right font-medium text-emerald-700">{formatMoney(r.amount)}</td>
                              <td className="p-2 text-right text-slate-600">{formatMoney(r.commission)}</td>
                              <td className="p-2 text-slate-500 max-w-[120px] truncate">{r.notes || '-'}</td>
                              {canEdit && (
                                <td className="p-2 text-center">
                                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:bg-red-50" onClick={() => handleDeleteRecharge(r.id)} data-testid={`delete-recharge-${i}`}>
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex justify-end gap-6 text-sm font-semibold pt-3 border-t mt-3">
                      <span className="text-emerald-700">Total: {formatMoney(selectedPhone.totalRechargeAmount)}</span>
                      <span className="text-slate-500">Commission: {formatMoney(selectedPhone.recharges.reduce((s, r) => s + (r.commission || 0), 0))}</span>
                    </div>
                  </>
                ) : (
                  <div className="py-8 text-center text-slate-400 text-sm">No recharges yet. Add the first one above.</div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Recharge Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={(open) => { if (!bulkSubmitting) setShowBulkDialog(open); }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-indigo-500" />
              Bulk Recharge - {selectedPhones.length} Phone(s)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Selected phones summary */}
            <div className="max-h-36 overflow-y-auto bg-slate-50 rounded-lg p-3 space-y-1">
              {selectedPhones.map((p) => (
                <div key={`${p.phoneNumber}-${p.vendorName}`} className="flex justify-between text-xs">
                  <span className="font-mono font-medium">{p.phoneNumber}</span>
                  <span className="text-slate-500">{p.companyName || p.bankName} - {p.vendorName}</span>
                </div>
              ))}
            </div>

            {/* Recharge Form */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Amount *</Label>
                <Input type="number" step="0.01" placeholder="0.00" value={bulkForm.amount} onChange={(e) => setBulkForm(p => ({ ...p, amount: e.target.value }))} data-testid="bulk-amount-input" />
              </div>
              <div>
                <Label className="text-xs">Commission</Label>
                <Input type="number" step="0.01" placeholder="0.00" value={bulkForm.commission} onChange={(e) => setBulkForm(p => ({ ...p, commission: e.target.value }))} data-testid="bulk-commission-input" />
              </div>
              <div>
                <Label className="text-xs">Date</Label>
                <Input type="date" value={bulkForm.date} onChange={(e) => setBulkForm(p => ({ ...p, date: e.target.value }))} data-testid="bulk-date-input" />
              </div>
              <div>
                <Label className="text-xs">Payment Method</Label>
                <Select value={bulkForm.paymentMethod} onValueChange={(val) => setBulkForm(p => ({ ...p, paymentMethod: val }))}>
                  <SelectTrigger data-testid="bulk-payment-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PhonePe">PhonePe</SelectItem>
                    <SelectItem value="Google Pay">Google Pay</SelectItem>
                    <SelectItem value="Paytm">Paytm</SelectItem>
                    <SelectItem value="Net Banking">Net Banking</SelectItem>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Notes</Label>
                <Input placeholder="Optional notes (applied to all)" value={bulkForm.notes} onChange={(e) => setBulkForm(p => ({ ...p, notes: e.target.value }))} data-testid="bulk-notes-input" />
              </div>
            </div>

            {/* Progress */}
            {bulkSubmitting && (
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-slate-600">
                  <span>Processing...</span>
                  <span>{bulkProgress.done}/{bulkProgress.total}</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div
                    className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${bulkProgress.total ? (bulkProgress.done / bulkProgress.total) * 100 : 0}%` }}
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowBulkDialog(false)} disabled={bulkSubmitting}>Cancel</Button>
              <Button onClick={handleBulkRecharge} disabled={bulkSubmitting} className="gap-2 bg-indigo-600 hover:bg-indigo-700" data-testid="bulk-submit-btn">
                <Layers className="w-4 h-4" />
                {bulkSubmitting ? `Processing ${bulkProgress.done}/${bulkProgress.total}...` : `Recharge ${selectedPhones.length} Phone(s)`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PhoneNumbersPage;
